//buildDaily10Share elsewhere should be replaced by useHandleShare below

import { Share } from 'react-native'
import { router } from 'expo-router'

import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'

import { useStore as useMobXStore } from '@/models'
import { create } from 'zustand'
import { events } from '@/models/analytics/analytics-events'

import { formatPlayDate, formatIsoDateString } from '@/utils/dateFormatter'
import { formatDaily10Score } from '@/utils/daily10Share'

import { useThrottledCallback } from 'use-debounce'

const { 
  DEFAULT_GAME_NAME: GAME_NAME, 
  DEFAULT_GAME_NAME_PERSONALIZED,
  DEFAULT_MAX_HANDS, 
  DEFAULT_FREE_PREFLOP_FOLDS, 
  DEFAULT_BOT_COUNT, 
  DEFAULT_STARTING_CHIPS,
  NAVIGATION_THROTTLE_MS 
} = GAME_CONSTANTS.DAILY10_CONSTANTS

import { defaultTheme, purpleTheme, greenTheme, defaultImageTheme, purpleImageTheme, greenImageTheme } from "@/components/Mini/theme"

type StoreState = {
    GAME_NAME: string
    DEFAULT_GAME_NAME_PERSONALIZED: string
    DEFAULT_STARTING_CHIPS: number
    DEFAULT_MAX_HANDS: number
    DEFAULT_FREE_PREFLOP_FOLDS: number
    DEFAULT_BOT_COUNT: number
    playedDate: string
    setPlayedDate: () => void
    error: string | null
    setError: (message: string | null) => void
    isStartingGame: boolean
    setIsStartingGame: (value: boolean) => void
    theme: typeof defaultTheme
    setTheme: (themeName: string) => void
}

const useGameStore = create<StoreState>((set, get) => ({
  GAME_NAME,
  DEFAULT_GAME_NAME_PERSONALIZED,
  DEFAULT_STARTING_CHIPS,
  DEFAULT_MAX_HANDS, 
  DEFAULT_FREE_PREFLOP_FOLDS, 
  DEFAULT_BOT_COUNT, 
  //ROUND_COUNT,
  playedDate: formatPlayDate(),
  setPlayedDate: () => set({ playedDate: formatPlayDate() }),
  error: null,
  setError: (message) => set({ error: message }),
  isStartingGame: false,
  setIsStartingGame: (value) => set({ isStartingGame: value }),
  theme: defaultImageTheme,
  setTheme: (themeName) => {
    if (themeName === 'default') {
      set({ theme: defaultTheme})
    } else if (themeName === 'purple'){
      set({ theme: purpleTheme })
    } else if (themeName === 'green'){
      set({ theme: greenTheme })
    } else if (themeName == 'defaultImage'){
      set({theme: defaultImageTheme})
    } else if (themeName == 'purpleImage'){
      set({theme: purpleImageTheme})
    } else if (themeName == 'greenImage'){
      set({theme: greenImageTheme})
    }
  }
}))


 function useStore<T>(selector: (s: StoreState) => T): T {
  return useGameStore(selector)
}


 function useD10User(){
  
  const { session: { user, isAdmin, isSuperAdmin }, analytics: { setEvent } } = useMobXStore()
  
  return {
    userId: user?.auth0Id ?? null,
    userName: user?.name ?? null,
    isAdmin,
    isSuperAdmin,
    setEvent
  }
}

function useD10QueryStatus(){

  const { userId } = useD10User()
  const playedDate = useStore(state => state.playedDate)

  const status = useQuery(api.games.queries.getDaily10Status, userId ? { userId, playedDate } : 'skip')

  return {status};

}

//leaderboard
function useD10QueryLeaderboard(){
    
  const playedDate = useStore(state => state.playedDate)

  const leaderboard = useQuery(api.games.queries.getDaily10Leaderboard, { playedDate })

  return {leaderboard};

}

//log screen
function useD10QueryLog(){
    
  const { userId } = useD10User()

  const log = useQuery(api.games.queries.getDaily10Log, userId ? { userId } : 'skip')

  return { log };

}

//streaks card
function useD10QueryHistory(){

  const { userId } = useD10User()
  const playedDate = useStore(state => state.playedDate)

  const stats = useQuery(api.games.queries.getDaily10History, userId ? { userId, todayDate: playedDate } : 'skip')

  return { stats }

}

function useD10QueryDiamonds(){
  const category="daily10"
  const playedDate = useStore((state) => state.playedDate)
  const diamondEvents = useQuery(api.diamonds.queries.getDiamondEvents, {
    category,
    refId: playedDate
  });

  return {
    events: diamondEvents?.events,
    diamonds: diamondEvents?.total ?? 0
  }
}


//admin handlers
function useHandleTestingReset(){
  const { userId } = useD10User()
  const playedDate = useStore(state => state.playedDate)
  const resetDaily10 = useMutation(api.games.mutations.resetDaily10ForTesting)

  const handleTestingReset = async () => {
    if (!userId) return
    await resetDaily10({ userId, playedDate })
  }

  return handleTestingReset
}

function useHandleSeedLeaderboard(){
  const playedDate = useStore(state => state.playedDate)
  const seedLeaderboard = useMutation(api.games.mutations.seedDaily10Leaderboard)

  const handleSeedLeaderboard = async () => {
    await seedLeaderboard({ count: 20, playedDate })
  }

  return handleSeedLeaderboard
}

function useHandleClearLeaderboardSeeds(){
  const playedDate = useStore(state => state.playedDate)
  const clearLeaderboardSeeds = useMutation(api.games.mutations.clearDaily10LeaderboardSeedData)

  const handleClearLeaderboardSeeds = async () => {
    await clearLeaderboardSeeds({ playedDate })
  }

  return handleClearLeaderboardSeeds
}


//nav handlers 

function useHandleModule({ event }: { event?: string } = {}){
  
  const { userId, setEvent } = useD10User()
  const playedDate = useStore(state => state.playedDate)

  const handleModule = useThrottledCallback(() => {
    
    if (event){
      setEvent(event, {
        userId,
        playedDate
      })
    }

    router.push('/(root)/daily10')

  }, NAVIGATION_THROTTLE_MS)

  return handleModule
}

function useHandleClose(){

  const handleClose = useThrottledCallback(() => {
    router.back()
   }, NAVIGATION_THROTTLE_MS)

  return handleClose
}

function useHandleHome(){

  const handleHome = useThrottledCallback(() => {
    
    router.dismissTo('/(root)/daily10')

  }, NAVIGATION_THROTTLE_MS)

  return handleHome

}


function useHandlePlay(){

    const { userId, userName } = useD10User()

    //const playedDate = useStore(state => state.playedDate)
    const playedDate = formatPlayDate()

    const setError = useStore(s => s.setError)
    const setIsStartingGame = useStore(s => s.setIsStartingGame)

    const createDaily10Game = useMutation(api.games.mutations.createDaily10Game)

    const handlePlay = useThrottledCallback(async () => {
      if (!userId || !userName) return
      try {
        setError(null)
        setIsStartingGame(true)

        const result = await createDaily10Game({ userId, playedDate, name: userName })
        if (!result?.gameId) throw new Error('Failed to create game')

        const { gameId } = result

        router.push(`/game/${gameId}`)
        // Don't reset isLoading — keep the screen in loading state through the
      // navigation transition so the in_progress card doesn't flash
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start game'
        setError(message)
        //setIsLoading(false)
        setIsStartingGame(false)
      }
    }, NAVIGATION_THROTTLE_MS)

    return handlePlay
  }

function useHandleResume(){

  const { status } = useD10QueryStatus();

  const handleResume = useThrottledCallback(() => {
    if (status?.status !== 'in_progress') return
    const { gameId } = status
    router.push(`/game/${gameId}`)
  }, NAVIGATION_THROTTLE_MS)

  return handleResume;
}

function useHandleResults(){

  const { userId, setEvent } = useD10User()
  const playedDate = useStore(state => state.playedDate)

  const handleResults = useThrottledCallback(() => {
    setEvent(events.daily10.results_tapped, {
      userId,
      playedDate
    })
    router.push({ pathname: '/(root)/daily10/results' })
  }, NAVIGATION_THROTTLE_MS)

  return handleResults;
}

function useHandleLeaderboard(){

  const { userId, setEvent } = useD10User()
  const playedDate = useStore(state => state.playedDate)

  const handleLeaderboard = useThrottledCallback(() => {
    setEvent(events.daily10.leaderboard_tapped, {
      userId,
      playedDate
    })
    router.push('/(root)/daily10/leaderboard')
  }, NAVIGATION_THROTTLE_MS)

  return handleLeaderboard;

}

function useHandleLog(){

  const handleLog = useThrottledCallback(() => {
    router.navigate('/(root)/daily10/log')
  }, NAVIGATION_THROTTLE_MS)

  return handleLog 
}

//handlers
/*function useHandleShare(){

  //const playedDate = useStore(state => state.playedDate)

  const { status } = useD10QueryStatus();

  const handleShare = async () => {

    if (status?.status !== 'completed') return

    const { result } = status

    const text = buildDaily10ShareMessage({handOutcomes: result.handOutcomes, playedDate: result.playedDate, finalChips: result.finalChips, streak: status.streak})
    
    try {
      await Share.share({ message: text })
    } catch {
      // User cancelled
    }
  }

  return handleShare;
}*/



function useHandleShare() {
    const { userId, setEvent } = useD10User()
    //const playedDate = useStore((state) => state.playedDate)
    //const gameName = useStore(s => s.GAME_NAME)
    const gameName = useStore(s => s.DEFAULT_GAME_NAME_PERSONALIZED)
    const { status } = useD10QueryStatus()

    const handleShare = async () => {
      if (status?.status !== 'completed') return

      const { result, streak } = status;
      const { handOutcomes, playedDate, finalChips, countedHands } = result;

      const outcomeEmoji = {
        free_fold: '✖️ ',
        win: '🟢',
        loss: '🔴'
      }
      
      const grid = handOutcomes.map((o) => outcomeEmoji[o]).join('')

      // const timeStr = totalElapsedMs != null ? ` in ${formatMs(totalElapsedMs)}` : ''
      const dateStr = formatIsoDateString(playedDate)
  
      const shareText =
        `${gameName} - ${dateStr}\n` +
        `${grid}\n` +
        `Score: ${formatDaily10Score(finalChips)} | Streak: ${streak}\n`+
        `\n` +
        `Play at pokerpower.com`

      
      setEvent(events.daily10.results_share_tapped, {
        userId,
        playedDate,
        finalChips: formatDaily10Score(finalChips),
        totalHands: countedHands
      })

      try {
        await Share.share({ message: shareText })
      } catch {
        // User cancelled
      }
    }

    return handleShare
}

export type { StoreState }
export {
  //useGameStore,
  useStore, //zustand vars stable across pages
  useD10User, //eventually useCurrentUser as a general hook and separate call to useStore for setEvents
  useD10QueryStatus, //reusable query with params
  useD10QueryStatus as useQueryStatus, //move these all to no namespace
  useD10QueryLeaderboard, //
  useD10QueryLog, //
  useD10QueryHistory, //
  useD10QueryDiamonds,
  useHandleTestingReset, //admin handler
  useHandleSeedLeaderboard, //admin handler
  useHandleClearLeaderboardSeeds, //admin handler
  useHandleModule, //nav handler
  useHandleClose, //
  useHandleHome, //
  useHandlePlay, //
  useHandleResume, // 
  useHandleResults, // 
  useHandleLeaderboard, //
  useHandleLog, //
  useHandleShare // handler 
}
